import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, SLOT_FUND, WRITE_FLAGS } from "./_shared.js";

/**
 * `EXP-F-NOALLOC` (@UC0202) — ghi khoản chi **QUỸ TRẢ**, không phân bổ cho ai, T2.
 * Executor = `expense.create` ⇒ `ExpenseService.create` (`POST /v1/funds/:id/expenses`) — **cùng cửa với màn ghi chi**.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
 * 🔴🔴 ĐỌC TRƯỚC KHI "SỬA LẠI CHO ĐÚNG": trang tiền này **CỐ Ý KHÔNG CÓ Ô "NGƯỜI ỨNG"**.
 *
 * Nhìn thoáng thì đây có vẻ vi phạm CLAUDE.md §3.6 (*"`payerMemberId` khai TƯỜNG MINH lúc tạo … NULL ⇒ 422 fail-loud"*).
 * Không phải. §3.6 cấm **SUY** người ứng từ người bấm nút; nó **không bắt BỊA** ra một người ứng.
 *
 *  · `EXP-F-NOALLOC` = **`fund_flow`**: quỹ trả 100%, **KHÔNG AI ỨNG TIỀN CẢ**. `payerMemberId = null` ở đây chính là
 *    **SỰ THẬT**, không phải một chỗ trống bị bỏ quên.
 *  · Cửa GUI tự enforce điều đó theo CẤU TRÚC — `expense.service.ts` (money-engine):
 *        `participants.length > 0 && !fundBearsAllShares ? resolvePayerMemberId(…) : null`
 *    0 participant ⇒ nhánh `null`. Trang này không gửi participant nào, nên không có đường nào ghi ra một chủ nợ.
 *  · Ca §3.6 *"NULL ⇒ 422"* là ca **peer/mixed**: có người ứng THẬT mà quên khai. Đó là chuyện của `EXP-F-EQUAL` (0042),
 *    **không phải** của trang này.
 *
 * ⇒ Thêm một ô "ai ứng tiền" vào đây là **ép ghi một cạnh nợ KHÔNG TỒN TẠI** ("nhóm nợ X" với X chẳng ứng gì) —
 *   sai cùng hướng với chính cái bug §3.6 sinh ra để chặn. Muốn ghi người ứng thì dùng trang chia (0042).
 *
 * 🔴 VÀ VÌ SAO KHÔNG PHẢI `emergencyExpense.create` (Sổ bản 0013 từng khai thế — đo lại ở B0 của 0041):
 *   `EmergencyExpenseService._createInTx` **KHÔNG nhận `payerMemberId` từ input**; nó tìm
 *   `groupMember.findFirst({ primaryUserId: input.actorUserId })` (403 nếu người bấm không có ghế) rồi **công bố người đó
 *   là `payerMemberId` trong sự kiện outbox `expense.emergency_advanced`** — tức **SUY TỪ NGƯỜI ĐANG BẤM**. Thủ quỹ gõ
 *   *"ghi chi 300k tiền nước, quỹ trả"* ⇒ thông báo nói **thủ quỹ đã ứng 300k**. Đúng "bug đắt nhất repo" (§3.6).
 *   Cửa đó còn đánh dấu `isEmergency`, đòi bằng chứng, và mở **phiên truy nhận** (consensus + hạn chót; §3.8: hết hạn ⇒
 *   LUÔN `failed`) — hành vi khác hẳn "ghi chi thường".
 *   ⚠️ ĐÍNH CHÍNH một lời khai dễ chép nhầm (đo lại khi chạy đột biến M1 của 0041): cửa khẩn cấp **KHÔNG ghi
 *   `payerMemberId` xuống hàng `expenses`** — hàng của HAI cửa giống hệt nhau ở cột đó (cả hai `null`). Nên một phép đo
 *   chỉ nhìn cột ấy là **lưới MÙ**: đột biến trỏ binding sang cửa khẩn cấp đã SỐNG SÓT ở vòng đo đầu. Chỗ đo thật:
 *   `isEmergency` · trạng thái phiên đồng thuận · payload outbox (xem `ai-doer-exp-f-noalloc.int-spec.ts`).
 *   ⚠️ `exp-f-equal.ts` (0042) **vẫn đang khai `emergencyExpense.create`** — xem bàn giao ở hồ sơ 0041.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * 🔴 US-AIV2-0112 H09 (@UC0202, hồ sơ 0112 §H09 mục ⭐): câu "mua/chi/hết + số tiền" KHÔNG nói ứng/chia ⇒ **trang này (quỹ trả)**.
 * Đó là lựa chọn tường minh của TRANG, không phải đoán người: phiếu in *"quỹ trả, không ai ứng tiền?"* và người phải bấm Đồng ý;
 * ai đã ứng thật thì Huỷ rồi nói "X ứng, chia đều" ⇒ trang chia (0042), nơi người ứng khai tường minh (§3.6). Câu nói ứng/chia
 * vẫn về trang chia — chiều ngược có ca ở `exp-f-noalloc.page.test.ts`.
 */
export const EXP_F_NOALLOC = {
  id: "EXP-F-NOALLOC",
  uc: "@UC0202",
  tier: "T2",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    { ...SLOT_FUND, clarify: "Chi từ quỹ nào?" },
    { id: "soTien", type: "money", required: true, ask: "Chi bao nhiêu?" },
    { id: "noiDung", type: "text", required: true, ask: "Chi cho việc gì?" },
    { id: "ngay", type: "date", required: false, ask: "Chi ngày nào? (bỏ trống = hôm nay)" },
  ],
  llm: {
    description: "Ghi một khoản chi lấy từ quỹ chung — quỹ trả, không chia cho ai và không ai ứng tiền trước.",
    examples: [
      "ghi chi 300k tiền nước, quỹ trả",
      "ghi chi 300k tien nuoc quy tra",
      "quỹ chi 2 triệu mua quà tết cho cô",
      "quy chi 2 trieu mua qua tet",
      "ghi khoản chi photo tài liệu 150k",
      "ghi khoan chi photo 150k",
      "chi 500k",
      "quỹ trả tiền nước tháng này",
      "ghi chi",
      "tiền điện tháng này 800k, quỹ trả",
      "chi từ quỹ",
      "ghi chi 200k",
      // ——— 0105: thêm cho đủ 40 ———
      "quỹ thanh toán 900k tiền sửa quạt",
      "quy thanh toan 900k tien sua quat",
      "chi tu quy",
      "quy tra tien nuoc thang nay",
      "tien dien thang nay 800k quy tra",
      "ghi chi 1 triệu mua bàn ghế, lấy từ quỹ",
      "ghi chi 1 trieu mua ban ghe lay tu quy",
      "quỹ chi 450k tiền in giấy khen",
      "quy chi 450k tien in giay khen",
      "trừ quỹ 600k mua nước uống",
      "tru quy 600k mua nuoc uong",
      "ghi khoản chi 2tr thuê xe, quỹ trả hết",
      "ghi khoan chi 2tr thue xe quy tra het",
      "chi tiền thuê sân 300k từ quỹ đội",
      "chi tien thue san 300k tu quy doi",
      "lấy quỹ trả 150k tiền photo",
      "lay quy tra 150k tien photo",
      "ghi chi mua quà 800k không chia cho ai",
      "ghi chi mua qua 800k khong chia cho ai",
      "quỹ trả 1tr2 tiền trang trí lớp",
      "quy tra 1tr2 tien trang tri lop",
      "ghi một khoản chi từ quỹ chung",
      "ghi mot khoan chi tu quy chung",
      "chi 250k tiền bánh kẹo, quỹ lo",
      "chi 250k tien banh keo quy lo",
      "ghi chi tiền vệ sinh 200k quỹ trả",
      "ghi chi tien ve sinh 200k quy tra",
      "trích quỹ chi 3 triệu tổ chức trung thu",
      "trich quy chi 3 trieu to chuc trung thu",
      /*
       * 🔴 US-AIV2-0112 H09 (@UC0202) — câu KỂ "mua … (cho ai) hết …" KHÔNG nói chia/ứng ⇒ quỹ trả. Trước H09 dạng câu này chỉ
       * có ở trang CHIA ("mua đồ hết 620k, cưa đều ra") ⇒ "mua nước cho các con hết 150k" ra EXP-F-EQUAL, "hôm nay mua hoa tặng
       * cô 300 nghìn" ra menu (probe 28/09). Câu nghiệm thu (A07) cố ý KHÔNG chép vào đây — `exp-f-noalloc.page.test.ts`.
       * Chọn chữ bằng đo top-8 OFFLINE (`_runs/US-AIV2-0112/H09`): chỉ dùng từ trang này ĐÃ có + "hoa" ⇒ 12 ca golden đổi tập
       * top-8, 0 trang đích rơi (bản đầu có "ảnh/nghìn/các bạn" ⇒ 117 ca, kéo trang này vào câu đợt thu/đổi ảnh).
       * ⚠️ ÍT câu là CÓ CHỦ Ý: bản 8 câu làm trang DÀI ra ⇒ BM25 chuẩn hoá độ dài đẩy câu golden sát ngưỡng "chi 500k" xuống DƯỚI
       * EXP-F-EQUAL ⇒ eval thật: model trả unknown (amount 39/40 < 99%). Thêm câu vào đây ⇒ chạy lại `pair-flip.mjs` trước.
       */
      "mua nước uống cho đội hết 300k",
      "mua nuoc uong cho doi het 300k",
      "mua hoa cho cô hết 200k",
      "mua hoa cho co het 200k",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    heldOutExamples: [
      "quy chi 200k mua nuoc da cho doi nha",
      "lấy tiền quỹ trả 500k tiền thuê loa ạ",
      "tui xuat quy 1tr mua qua cho thay",
      "trích quỹ mua băng rôn 300k nhé chị Hai",
      "ghi giup khoan chi tien in an 120k, quy lo het",
      "tien gui xe hom nay 50k, rut quy tra",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["tháng này chi những khoản nào?"],
  },
  /**
   * 🔴 US-AIV2-0110 S03 (@UC0260 · QĐ PO 4) — ĐÁP ÁN TIỀN của bộ eval, VIẾT TAY từ chính câu (không từ đầu ra model).
   * KHÔNG vào prompt ⇒ không đổi hash theo ca. Câu vắng mặt ở đây: không nói số tiền.
   * Không khai: "1tr2" (#32/#33) — đuôi số ghép, sản phẩm HỎI LẠI (money-parser luật 4).
   */
  /**
   * 🔴 US-AIV2-0110 S19b (@UC0267) — BẬT lưới đỡ ô tiền: chi KHÔNG chia: câu nói một số tiền thì đó là số CHI — không có phần chia, không có "tổng vs mỗi người".
   * Tiêu chí + lý do ở `aiPageSchema.moneyFromSentence`. Không vào prompt ⇒ hash không đổi.
   */
  moneyFromSentence: "sole-amount",
  evalAmounts: {
    "ghi chi 300k tiền nước, quỹ trả": "300000",
    "ghi chi 300k tien nuoc quy tra": "300000",
    "quỹ chi 2 triệu mua quà tết cho cô": "2000000",
    "quy chi 2 trieu mua qua tet": "2000000",
    "ghi khoản chi photo tài liệu 150k": "150000",
    "ghi khoan chi photo 150k": "150000",
    "chi 500k": "500000",
    "tiền điện tháng này 800k, quỹ trả": "800000",
    "ghi chi 200k": "200000",
    "quỹ thanh toán 900k tiền sửa quạt": "900000",
    "quy thanh toan 900k tien sua quat": "900000",
    "tien dien thang nay 800k quy tra": "800000",
    "ghi chi 1 triệu mua bàn ghế, lấy từ quỹ": "1000000",
    "ghi chi 1 trieu mua ban ghe lay tu quy": "1000000",
    "quỹ chi 450k tiền in giấy khen": "450000",
    "quy chi 450k tien in giay khen": "450000",
    "trừ quỹ 600k mua nước uống": "600000",
    "tru quy 600k mua nuoc uong": "600000",
    "ghi khoản chi 2tr thuê xe, quỹ trả hết": "2000000",
    "ghi khoan chi 2tr thue xe quy tra het": "2000000",
    "chi tiền thuê sân 300k từ quỹ đội": "300000",
    "chi tien thue san 300k tu quy doi": "300000",
    "lấy quỹ trả 150k tiền photo": "150000",
    "lay quy tra 150k tien photo": "150000",
    "ghi chi mua quà 800k không chia cho ai": "800000",
    "ghi chi mua qua 800k khong chia cho ai": "800000",
    "chi 250k tiền bánh kẹo, quỹ lo": "250000",
    "chi 250k tien banh keo quy lo": "250000",
    "ghi chi tiền vệ sinh 200k quỹ trả": "200000",
    "ghi chi tien ve sinh 200k quy tra": "200000",
    "trích quỹ chi 3 triệu tổ chức trung thu": "3000000",
    "trich quy chi 3 trieu to chuc trung thu": "3000000",
    // US-AIV2-0112 H09 (@UC0202)
    "mua nước uống cho đội hết 300k": "300000",
    "mua nuoc uong cho doi het 300k": "300000",
    "mua hoa cho cô hết 200k": "200000",
    "mua hoa cho co het 200k": "200000",
  },
  executor: "expense.create",
  /** AI-INTERP M13 (@UC0173): ca eval GIỮA FORM (trang này đang mở) — Sổ là nguồn; không vào prompt/BM25. Bộ thu dựng prompt như cổng. */
  evalInForm: [
    { askedSlot: "soTien", filledSlots: ["noiDung"], history: [{ role: "user", content: "ghi chi tiền nước, quỹ trả" }, { role: "assistant", content: "Chi bao nhiêu?" }], input: "150k", expect: { kind: "inFormAnswer", slot: "soTien" } },
    { askedSlot: "noiDung", filledSlots: ["soTien"], input: "tiền photo", expect: { kind: "inFormAnswer", slot: "noiDung" } },
    { askedSlot: "ngay", filledSlots: ["soTien", "noiDung"], input: "15/11", expect: { kind: "inFormAnswer", slot: "ngay" } },
    { askedSlot: "ngay", filledSlots: ["soTien", "noiDung"], history: [{ role: "user", content: "quỹ chi 200k tiền nước" }, { role: "assistant", content: "Chi ngày nào? (bỏ trống = hôm nay)" }], input: "à nhầm, 250k chứ", expect: { kind: "inFormAnswer", slot: "soTien" } },
    { askedSlot: "noiDung", filledSlots: ["soTien"], input: "thôi bỏ đi", expect: { kind: "cancel" } },
  ],
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: cửa ghi chi; `EXP-F-EQUAL` là biến thể cùng cửa nên khai rỗng. */
  endpoints: [
    "POST /funds/{fundId}/expenses",
  ],
  facts: ["tenQuy", "ngayGhi", "ketQua", "noiDungGhi"],
  templates: {
    /**
     * 🔴 M1b (review #871) — **`{noiDungGhi}`, KHÔNG phải `{noiDung}`.**
     *
     * `{noiDung}` đến từ `labelValues()` → `slots.noiDung.label`, mà `resolveTextSlot` chỉ `trim()` ⇒ **thẻ in bản
     * THÔ trong khi sổ ghi bản ĐÃ CẠO**. Đo được: ca RLO ⇒ thẻ còn `U+202E`/`U+202C`, `expenses.name` sạch. Và
     * `FACTS_MISMATCH` **không** bắt được, vì `noiDung` đi đường `labelValues` chứ không đi đường facts.
     * Mâu thuẫn thẳng với AC2 `@UC0163` (*"dòng sổ khớp phiếu từng chữ"*) — chỉ theo chiều ngược với chiều mà
     * docblock `cleanUserText` lo (ở đó là "đừng cắt"; ở đây là "đừng in thứ khác thứ sẽ ghi").
     */
    confirm: "Ghi chi {soTien} “{noiDungGhi}” từ quỹ {tenQuy}, ngày {ngayGhi} — quỹ trả, không ai ứng tiền?",
    /**
     * 🔴🔴 CRITICAL-1 (review #871) — **KHÔNG có chữ "trừ tương ứng" nào ở đây nữa.** Kết cục do `status` mà
     * `ExpenseService.create` TRẢ VỀ quyết, và nó có **ba** giá trị, hai trong đó nghĩa là **chưa đồng nào rời quỹ**
     * (`pending_decision` · `cooling`). Câu tĩnh cũ khẳng định quỹ đã trừ **cho cả ba** ⇒ với nhóm bật chế độ cần
     * duyệt, người rà đo trên DB thật `ledger_lines +0` mà trợ lý vẫn báo *"Quỹ … trừ tương ứng"*.
     *
     * ⇒ hệ quả nằm trong **GIÁ TRỊ fact `{ketQua}`** — đúng khuôn `trangThaiSau` của `@UC0198`, và đúng luật
     * *"tầng trình bày không bịa câu"*: câu đến từ một catalog đóng ở server (`ketQuaGhiChi`), không từ template.
     */
    done: "Đã ghi chi {soTien} “{noiDungGhi}”. {ketQua}",
    /**
     * 🔴 MEDIUM-4 (review #871): `renderClarify` dùng câu này cho **MỌI** ô nhập nhằng, không riêng ô quỹ. Câu cũ
     * (*"quỹ trả thẳng, hay chia đều…"*) là câu **CHỌN TRANG**, nên lượt làm rõ ô **quỹ** hiện ra nó kèm hai nút
     * QUỸ — hỏi một đằng, chọn một nẻo. Mọi trang bound khác viết câu theo Ô; câu A-hay-B thuộc `ambiguities`.
     */
  },
  labels: {
    soTien: "Số tiền",
    noiDung: "Nội dung",
    tenQuy: "Quỹ",
    ngayGhi: "Ngày chi",
    ketQua: "Kết quả",
    noiDungGhi: "Nội dung",
  },
  journey: { outcomes: { done: { next: ["READ-FUND-MONEY"] } } },
  /**
   * 🔴 AI-SO-DRIFT-1012 (@UC0241 @UC0034 · QĐ người dùng 12/10/2026) — thành viên KHÔNG ghi chi được (vai giữ O/A/T — `roles-parity`), nhưng GỬI
   * YÊU CẦU CHI được: màn nhóm, nút «Chi quỹ» (`group_action_bars.dart` nhánh member) ⇒ `POST /funds/:id/expenses`, server ép CHỜ DUYỆT
   * (`ExpenseService.create` — yêu cầu của thành viên không bao giờ tự vào sổ). Câu khoá theo vai trước đó bảo "nhắn chủ nhóm" — sai từ 12/10.
   * Chỉ trang QUỸ TRẢ: yêu cầu chi không mang khối chia/người ứng (`memberRequestForbiddenFields`) ⇒ `EXP-F-EQUAL` giữ câu cũ.
   */
  roleLockedExit: "bạn gửi yêu cầu chi được: ở màn hình nhóm bấm «Chi quỹ», yêu cầu chờ duyệt xong quỹ mới chi.",
  help: { title: "Ghi chi (quỹ trả)", tryNow: "ghi chi 300k tiền nước, quỹ trả" },
} satisfies AiCapabilityPage;
